import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatNumber, formatDate } from "@/utils/format";
import type { InstagramFollowersDayPoint } from "@/services/marketing";
import { lineDots, TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

interface Props {
  data: InstagramFollowersDayPoint[];
  loading?: boolean;
}

function formatChartDate(iso: string): string {
  const raw = (iso ?? "").trim().split("T")[0] ?? "";
  const parts = raw.split("-");
  if (parts.length < 3) return raw;
  return `${parts[2]}/${parts[1]}`;
}

function FollowersTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: InstagramFollowersDayPoint & { label?: string } }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="min-w-[180px] rounded-xl border border-slate-200 bg-white p-3 shadow-modal">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {formatDate(row.date)}
      </p>
      <p className="text-sm text-slate-700">
        Seguidores: <span className="font-semibold">{formatNumber(row.followers)}</span>
      </p>
    </div>
  );
}

export function InstagramFollowersByDayChart({ data, loading }: Props) {
  const chartData = data.map((row) => ({
    ...row,
    label: formatChartDate(row.date),
  }));

  return (
    <ChartContainer
      title="Evolução de seguidores"
      subtitle="Instagram — total acumulado por dia"
      loading={loading}
      empty={!loading && chartData.length === 0}
      emptyMessage="Sem dados de seguidores no período."
    >
      <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
        <YAxis tick={{ fontSize: 11, fill: "#64748b" }} width={52} />
        <Tooltip content={<FollowersTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
        <Legend formatter={() => "Seguidores"} />
        <Line
          type="monotone"
          dataKey="followers"
          name="followers"
          stroke="#6366f1"
          strokeWidth={2}
          {...lineDots("#6366f1")}
        />
      </LineChart>
    </ChartContainer>
  );
}
